import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { useCountDown, useI18N } from "@sundae/react-hooks";
import { Text, toolkitCx } from "@sundae/ui-toolkit";
import { ITextProps } from "@sundae/ui-toolkit/dist/types/components/atoms/Text/Text";
import cx from "classnames";
import { FC, memo, useCallback } from "react";
import { createPortal } from "react-dom";

import { EARNINGS_COUNTDOWN_ID } from "../../../constants/page.constants";
import { displayTimeValue, getNext2amUTCDate } from "../../../utils/time.utils";

interface IEarningsCountdownProps extends Pick<ITextProps, "size" | "variant"> {
  withProgress?: boolean;
}

export const CountdownComponent: FC<IEarningsCountdownProps> = ({
  size = "base",
  withProgress = true,
  variant,
}) => {
  const { t } = useI18N();
  const targetDate = getNext2amUTCDate();
  const [, { days, hours, minutes, seconds }] = useCountDown({
    targetDate,
  });
  const totalSecondsForCountdown = 24 * 60 * 60;
  const calculateCountdownProgress = useCallback(() => {
    const remainingSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
    return ((totalSecondsForCountdown - remainingSeconds) / totalSecondsForCountdown) * 100;
  }, [days, hours, minutes, seconds, totalSecondsForCountdown]);

  const countdownNode = document.getElementById(EARNINGS_COUNTDOWN_ID);

  const renderContent = useCallback(() => {
    if (!days && !hours && !minutes && !seconds) {
      return (
        <Text className="text-right" tag="div" size={size} variant="warning">
          <ExclamationTriangleIcon
            className={cx("inline-flex h-4 w-4", toolkitCx.layout.text.warning)}
          />{" "}
          {t("detail.countdown.ended")}
        </Text>
      );
    }

    return (
      <>
        <Text tag="span" size={size} variant={variant}>
          {[hours, minutes, seconds].map(displayTimeValue).join(":")}
        </Text>

        {withProgress && (
          <div className="absolute right-0 flex items-center px-1">
            <Text className="lowercase" tag="span" size={size} variant="silent">
              ({calculateCountdownProgress().toFixed(2)}% {t("done", { ns: "common" })})
            </Text>
          </div>
        )}
      </>
    );
  }, [days, hours, minutes, seconds]);

  return (
    countdownNode &&
    createPortal(
      <div className="flex w-full items-center">
        {withProgress && (
          <div
            className="absolute left-0 top-0 z-[-1] h-full rounded-br-lg rounded-tl-lg rounded-tr-lg bg-gradient-to-r from-secondary to-primary opacity-20"
            style={{ width: `${calculateCountdownProgress()}%` }}
          />
        )}
        {renderContent()}
      </div>,
      countdownNode
    )
  );
};

export const CountdownNode: FC = () => <div id={EARNINGS_COUNTDOWN_ID} />;

const Countdown: FC<IEarningsCountdownProps> = (props) => {
  return (
    <>
      <CountdownComponent {...props} />
      <CountdownNode />
    </>
  );
};

export default memo(Countdown);
